import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getProfile, updateProfile, changePassword, deleteAccount } from '../services/api';
import NavigationBar from '../components/NavigationBar';
import { sanitizeFormData } from '../utils/sanitization';

function ProfilePage({ token, onLogout }) {
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    badge_number: '',
    detachment: '',
    station_address: '',
    station_phone: '',
    station_email: '',
    approved: false,
    approval_date: null,
    approval_expires_at: null
  });

  const [originalProfile, setOriginalProfile] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [deleteData, setDeleteData] = useState({
    currentPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showVerificationWarning, setShowVerificationWarning] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});

  // Define field categories
  const restrictedFields = ['badge_number', 'detachment', 'station_address', 'station_phone', 'station_email'];

  const loadProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const data = await getProfile(token);
      const profileData = {
        username: data.username || '',
        email: data.email || '',
        badge_number: data.badge_number || '',
        detachment: data.detachment || '',
        station_address: data.station_address || '',
        station_phone: data.station_phone || '',
        station_email: data.station_email || '',
        approved: data.approved || false,
        approval_date: data.approval_date || null,
        approval_expires_at: data.approval_expires_at || null
      };
      setProfile(profileData);
      setOriginalProfile(profileData);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => onLogout(), 2000);
      } else {
        setError('Failed to load profile');
      }
    } finally {
      setProfileLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Check if restricted fields have changed
  const hasRestrictedFieldChanges = useMemo(() => {
    return restrictedFields.some(field => profile[field] !== originalProfile[field]);
  }, [profile, originalProfile, restrictedFields]);

  // Check if any fields have changed
  const hasAnyChanges = useMemo(() => {
    return Object.keys(profile).some(field => 
      !['approved', 'approval_date', 'approval_expires_at'].includes(field) && 
      profile[field] !== originalProfile[field]
    );
  }, [profile, originalProfile]);

  const handleProfileChange = useCallback((field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    
    // Track changes for restricted fields
    if (restrictedFields.includes(field)) {
      setPendingChanges(prev => ({ ...prev, [field]: value }));
    }
  }, [restrictedFields]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    // Check if restricted fields have changed for approved users
    if (profile.approved && hasRestrictedFieldChanges) {
      setShowVerificationWarning(true);
      return;
    }

    await submitProfileChanges();
  };

  const submitProfileChanges = async () => {
    setLoading(true);
    setMessage('');
    setError('');
    setShowVerificationWarning(false);

    try {
      const sanitizedProfile = sanitizeFormData(profile);
      const response = await updateProfile(sanitizedProfile, token);
      
      if (response.verificationRevoked) {
        setMessage('Profile updated successfully. Your verification has been revoked and requires admin re-approval for phone lookups.');
        // Reload profile to get updated approval status
        await loadProfile();
      } else {
        setMessage('Profile updated successfully');
      }
      
      // Update original profile to reflect new state
      setOriginalProfile(profile);
      setPendingChanges({});
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelRestrictedChanges = () => {
    // Revert restricted fields to original values
    const revertedProfile = { ...profile };
    restrictedFields.forEach(field => {
      revertedProfile[field] = originalProfile[field];
    });
    setProfile(revertedProfile);
    setPendingChanges({});
    setShowVerificationWarning(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPasswordMessage('');
    setPasswordError('');

    try {
      await changePassword(passwordData, token);
      setPasswordMessage('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      if (err.response?.data?.message) {
        setPasswordError(err.response.data.message);
      } else {
        setPasswordError('Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = useCallback((field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDeleteChange = useCallback((field, value) => {
    setDeleteData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDeleteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDeleteMessage('');
    setDeleteError('');

    try {
      await deleteAccount(deleteData, token);
      setDeleteMessage('Account deleted successfully');
      // Redirect to login after a short delay
      setTimeout(() => {
        onLogout();
      }, 2000);
    } catch (err) {
      if (err.response?.data?.message) {
        setDeleteError(err.response.data.message);
      } else {
        setDeleteError('Failed to delete account');
      }
    } finally {
      setLoading(false);
    }
  };

  // Using centralized auth utility for admin check
  const userIsAdmin = useMemo(() => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.is_admin === 1;
    } catch (error) {
      return false;
    }
  }, [token]);

  // Memoized styles to prevent unnecessary re-renders
  const styles = useMemo(() => ({
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px'
    }
  }), []);

  const sectionStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    marginBottom: '20px'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    margin: '8px 0',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    boxSizing: 'border-box'
  };



  const buttonStyle = {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1,
    marginRight: '10px',
    transition: 'all 0.3s ease',
    fontWeight: 'bold'
  };



  const errorStyle = {
    color: 'red',
    margin: '10px 0',
    padding: '10px',
    backgroundColor: '#f8d7da',
    borderRadius: '4px',
    border: '1px solid #f5c6cb'
  };

  const successStyle = {
    color: 'green',
    margin: '10px 0',
    padding: '10px',
    backgroundColor: '#d4edda',
    borderRadius: '4px',
    border: '1px solid #c3e6cb'
  };

  const formatHintStyle = {
    fontSize: '12px',
    color: '#666',
    fontStyle: 'italic',
    marginTop: '4px'
  };

  // Field styling based on editability
  const getFieldStyle = (fieldName) => {
    const baseStyle = {
      width: '100%',
      padding: '12px',
      margin: '8px 0',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      boxSizing: 'border-box'
    };

    if (restrictedFields.includes(fieldName)) {
      return {
        ...baseStyle,
        border: '2px solid #dc3545',
        backgroundColor: '#fff5f5',
        position: 'relative'
      };
    }

    return baseStyle;
  };

  const getFieldLabelStyle = (fieldName) => {
    const baseStyle = {
      display: 'block',
      marginBottom: '5px',
      fontWeight: 'bold'
    };

    if (restrictedFields.includes(fieldName)) {
      return {
        ...baseStyle,
        color: '#dc3545'
      };
    }

    return baseStyle;
  };

  const getFieldContainerStyle = (fieldName) => {
    if (restrictedFields.includes(fieldName)) {
      return {
        position: 'relative',
        padding: '15px',
        backgroundColor: '#fff5f5',
        borderRadius: '8px',
        border: '1px solid #f5c6cb',
        marginBottom: '20px'
      };
    }
    return {};
  };

  if (profileLoading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', margin: '50px 0' }}>Loading profile...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <NavigationBar 
        token={token} 
        onLogout={onLogout}
        title="Profile Settings"
        subtitle="Manage your account information"
      />

      {/* Account Status Section */}
      <div style={sectionStyle}>
        <h2 style={{ marginTop: 0, color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          Account Status
        </h2>
        
        <div style={{ 
          padding: '15px', 
          borderRadius: '4px', 
          border: '1px solid ' + (profile.approved ? '#28a745' : '#ffc107'),
          backgroundColor: profile.approved ? '#d4edda' : '#fff3cd',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ 
              fontWeight: 'bold', 
              color: profile.approved ? '#155724' : '#856404'
            }}>
              Status: {profile.approved ? '✅ Approved' : '⏳ Pending Approval'}
            </span>
          </div>
          
          {profile.approved ? (
            <div style={{ color: '#155724' }}>
              <p style={{ margin: '0 0 10px 0' }}>
                Your account is approved. You have full access to phone lookup features.
              </p>
              {profile.approval_date && (
                <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                  <strong>Approved:</strong> {new Date(profile.approval_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              )}
              {profile.approval_expires_at && (
                <div style={{ fontSize: '14px' }}>
                  <strong>Expires:</strong> {new Date(profile.approval_expires_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  {(() => {
                    const expirationDate = new Date(profile.approval_expires_at);
                    const now = new Date();
                    const daysUntilExpiration = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
                    
                    if (daysUntilExpiration <= 30 && daysUntilExpiration > 0) {
                      return (
                        <span style={{ color: '#856404', fontWeight: 'bold', marginLeft: '10px' }}>
                          ⚠️ Expires in {daysUntilExpiration} days
                        </span>
                      );
                    } else if (daysUntilExpiration <= 0) {
                      return (
                        <span style={{ color: '#721c24', fontWeight: 'bold', marginLeft: '10px' }}>
                          ❌ EXPIRED
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: '#856404' }}>
              <p style={{ margin: '0 0 10px 0' }}>
                Complete all profile fields below to receive approval.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Personal Information Section */}
      <div style={sectionStyle}>
        <h2 style={{ marginTop: 0, color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          Personal Information
        </h2>
        
        <form onSubmit={handleProfileSubmit}>
          {error && <div style={errorStyle}>{error}</div>}
          {message && <div style={successStyle}>{message}</div>}

          {/* Verification Warning Modal */}
          {showVerificationWarning && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{ color: '#dc3545', marginTop: 0, marginBottom: '20px' }}>
                  ⚠️ Verification Status Change Required
                </h3>
                <p style={{ marginBottom: '15px', lineHeight: '1.5' }}>
                  You are attempting to modify restricted fields that affect your verification status:
                </p>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  {Object.keys(pendingChanges).map(field => (
                    <li key={field} style={{ marginBottom: '5px' }}>
                      <strong>{field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> 
                      "{originalProfile[field] || 'Empty'}" → "{pendingChanges[field] || 'Empty'}"
                    </li>
                  ))}
                </ul>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '4px',
                  border: '1px solid #ffeaa7',
                  marginBottom: '20px'
                }}>
                  <strong>Impact:</strong> Your account verification will be revoked, and you will lose access to phone lookup features until an administrator re-approves your account.
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={cancelRestrictedChanges}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel Changes
                  </button>
                  <button
                    type="button"
                    onClick={submitProfileChanges}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Proceed & Revoke Verification
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Freely Editable Fields Section */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ color: '#28a745', marginBottom: '15px', borderBottom: '2px solid #28a745', paddingBottom: '5px' }}>
              ✅ Freely Editable Information
            </h3>
            <div style={{ padding: '15px', backgroundColor: '#d4edda', borderRadius: '8px', border: '1px solid #c3e6cb' }}>
              <p style={{ margin: '0 0 15px 0', color: '#155724', fontSize: '14px' }}>
                These fields can be updated without affecting your verification status.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={getFieldLabelStyle('username')}>
                    Username *
                  </label>
                  <input
                    style={getFieldStyle('username')}
                    value={profile.username}
                    onChange={e => handleProfileChange('username', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label style={getFieldLabelStyle('email')}>
                    Email *
                  </label>
                  <input
                    style={getFieldStyle('email')}
                    type="email"
                    value={profile.email}
                    onChange={e => handleProfileChange('email', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

                    {/* Restricted Fields Section */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ color: '#dc3545', marginBottom: '15px', borderBottom: '2px solid #dc3545', paddingBottom: '5px' }}>
              ⚠️ Restricted Fields (Requires Re-verification)
            </h3>
            <div style={{ padding: '15px', backgroundColor: '#f8d7da', borderRadius: '8px', border: '1px solid #f5c6cb' }}>
              <p style={{ margin: '0 0 15px 0', color: '#721c24', fontSize: '14px' }}>
                Changes to these fields will revoke your verification status and require administrative re-approval.
              </p>

              <div style={getFieldContainerStyle('badge_number')}>
                <label style={getFieldLabelStyle('badge_number')}>
                  Badge Number
                </label>
                <input
                  style={getFieldStyle('badge_number')}
                  value={profile.badge_number}
                  onChange={e => handleProfileChange('badge_number', e.target.value)}
                  placeholder="e.g., ABC-1234 or 12345"
                />
                <div style={formatHintStyle}>
                  Format suggestions: ABC-1234, 12345, B1234
                </div>
              </div>

              <div style={getFieldContainerStyle('detachment')}>
                <label style={getFieldLabelStyle('detachment')}>
                  Detachment/Department
                </label>
                <input
                  style={getFieldStyle('detachment')}
                  value={profile.detachment}
                  onChange={e => handleProfileChange('detachment', e.target.value)}
                  placeholder="e.g., Metro Police Department, County Sheriff"
                />
              </div>

              <h4 style={{ color: '#dc3545', marginTop: '25px', marginBottom: '15px' }}>Station Information</h4>

              <div style={getFieldContainerStyle('station_address')}>
                <label style={getFieldLabelStyle('station_address')}>
                  Station Address
                </label>
                <textarea
                  style={{ ...getFieldStyle('station_address'), height: '80px', resize: 'vertical' }}
                  value={profile.station_address}
                  onChange={e => handleProfileChange('station_address', e.target.value)}
                  placeholder="Enter complete station address including city, state, and ZIP code"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={getFieldContainerStyle('station_phone')}>
                  <label style={getFieldLabelStyle('station_phone')}>
                    Station Phone
                  </label>
                  <input
                    style={getFieldStyle('station_phone')}
                    value={profile.station_phone}
                    onChange={e => handleProfileChange('station_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                  <div style={formatHintStyle}>
                    Format: (555) 123-4567 or 555-123-4567
                  </div>
                </div>

                <div style={getFieldContainerStyle('station_email')}>
                  <label style={getFieldLabelStyle('station_email')}>
                    Station Email
                  </label>
                  <input
                    style={getFieldStyle('station_email')}
                    type="email"
                    value={profile.station_email}
                    onChange={e => handleProfileChange('station_email', e.target.value)}
                    placeholder="station@agency.gov"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Change Summary */}
          {hasAnyChanges && (
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              backgroundColor: hasRestrictedFieldChanges ? '#fff3cd' : '#d4edda', 
              borderRadius: '4px', 
              border: `1px solid ${hasRestrictedFieldChanges ? '#ffeaa7' : '#c3e6cb'}`,
              color: hasRestrictedFieldChanges ? '#856404' : '#155724'
            }}>
              <strong>Changes Summary:</strong>
              <ul style={{ margin: '10px 0 0 20px', padding: 0 }}>
                {Object.keys(profile).map(field => {
                  if (!['approved', 'approval_date', 'approval_expires_at'].includes(field) && 
                      profile[field] !== originalProfile[field]) {
                    return (
                      <li key={field} style={{ marginBottom: '5px' }}>
                        <strong>{field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> 
                        "{originalProfile[field] || 'Empty'}" → "{profile[field] || 'Empty'}"
                        {restrictedFields.includes(field) && profile.approved && (
                          <span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '10px' }}>
                            ⚠️ Requires re-verification
                          </span>
                        )}
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            </div>
          )}

          <div style={{ marginTop: '30px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button 
              type="submit" 
              style={{
                ...buttonStyle,
                backgroundColor: (hasRestrictedFieldChanges && profile.approved) ? '#dc3545' : '#007bff'
              }} 
              disabled={loading || !hasAnyChanges}
            >
              {loading ? 'Saving...' : (hasRestrictedFieldChanges && profile.approved) ? 'Save & Revoke Verification' : 'Save Profile'}
            </button>
            
            {hasAnyChanges && (
              <button 
                type="button"
                onClick={() => {
                  setProfile(originalProfile);
                  setPendingChanges({});
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Cancel Changes
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Password Change Section */}
      <div style={sectionStyle}>
        <h2 style={{ marginTop: 0, color: '#333', borderBottom: '2px solid #dc3545', paddingBottom: '10px' }}>
          Change Password
        </h2>

        <form onSubmit={handlePasswordSubmit}>
          {passwordError && <div style={errorStyle}>{passwordError}</div>}
          {passwordMessage && <div style={successStyle}>{passwordMessage}</div>}

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Current Password
            </label>
            <input
              style={inputStyle}
              type="password"
              value={passwordData.currentPassword}
              onChange={e => handlePasswordChange('currentPassword', e.target.value)}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                New Password
              </label>
              <input
                style={inputStyle}
                type="password"
                value={passwordData.newPassword}
                onChange={e => handlePasswordChange('newPassword', e.target.value)}
                required
              />
              <div style={formatHintStyle}>
                Minimum 6 characters
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Confirm New Password
              </label>
              <input
                style={inputStyle}
                type="password"
                value={passwordData.confirmPassword}
                onChange={e => handlePasswordChange('confirmPassword', e.target.value)}
                required
              />
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <button type="submit" style={{ ...buttonStyle, backgroundColor: '#dc3545' }} disabled={loading}>
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Account Section */}
      <div style={sectionStyle}>
        <h2 style={{ marginTop: 0, color: '#333', borderBottom: '2px solid #dc3545', paddingBottom: '10px' }}>
          Delete Account
        </h2>

        <div style={{ 
          padding: '15px', 
          borderRadius: '4px', 
          border: '1px solid #f5c6cb',
          backgroundColor: '#f8d7da',
          marginBottom: '20px'
        }}>
          <p style={{ margin: '0 0 10px 0', color: '#721c24' }}>
            <strong>⚠️ Warning:</strong> This action cannot be undone. Deleting your account will permanently remove all your data from the system.
          </p>
          <p style={{ margin: 0, color: '#721c24', fontSize: '14px' }}>
            You will lose access to all phone lookup features and your profile information will be permanently deleted.
          </p>
        </div>

        <form onSubmit={handleDeleteSubmit}>
          {deleteError && <div style={errorStyle}>{deleteError}</div>}
          {deleteMessage && <div style={successStyle}>{deleteMessage}</div>}

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Current Password
            </label>
            <input
              style={inputStyle}
              type="password"
              value={deleteData.currentPassword}
              onChange={e => handleDeleteChange('currentPassword', e.target.value)}
              required
              placeholder="Enter your current password to confirm deletion"
            />
            <div style={formatHintStyle}>
              You must enter your current password to confirm account deletion
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <button 
              type="submit" 
              style={{ 
                ...buttonStyle, 
                backgroundColor: '#dc3545',
                border: '2px solid #c53030'
              }} 
              disabled={loading || !deleteData.currentPassword}
            >
              {loading ? 'Deleting...' : 'Delete Account Permanently'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfilePage; 